import * as React from 'react'
import { ReactElement, useEffect, useMemo } from 'react'
import { makeStyles, MessageBar, tokens } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import Loading from '../../../../components/Loading'
import PartyConnectButton from '../../../components/Button/PartyConnectButton'
import { useSettings } from '../../../../hooks/useSettings'
import { BreakPointSizeMap, Viewport } from '../../../../hooks/useViewport'
import { FilePartyRoleDto } from '../../../../context/GlobalContext'
import { FilePartyRole, MainDto } from '../../../../config/filePartyConfig'
import { IFileDto } from '../../../../config/fileConfig'
import { IRootOverviewItemDto } from '../../../../config/documentConfig'
import FilePartySection from './FilePartySection'

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalM,
        // Sidepanel-first: keep the cards readable and stop them stretching on wide desktop widths.
        width: '100%',
        maxWidth: `${BreakPointSizeMap.get(Viewport.OutlookNewWide)}px`,
    },
    alert: {
        marginLeft: tokens.spacingHorizontalS,
        marginRight: tokens.spacingHorizontalS,
    },
    add: {
        paddingLeft: tokens.spacingHorizontalS,
    },
})

const matchesRole = (role: FilePartyRoleDto, party: MainDto): boolean =>
    party.role === role.type || (!party.role && role.type === FilePartyRole.Other)

export type FilePartyOverviewProps = {
    data: MainDto[]
    file: IFileDto
    documents?: IRootOverviewItemDto
    reset?: () => void
}

/**
 * v9 dossier partijenoverzicht. Groups the dossier's parties per soort (from tenant settings.filePartyRoles),
 * hides empty soorten, and renders each bedrijf with its contactpersonen as a card. Responsive width capping is
 * handled here; per-card reflow lives in FilePartyCard via useViewport.
 */
export default function FilePartyOverview(props: FilePartyOverviewProps): ReactElement {
    const styles = useStyles()
    const { settings } = useSettings()
    const { t: translate } = useTranslation()
    const location = useLocation<{ reload: boolean }>()

    const roles: FilePartyRoleDto[] = useMemo(() => settings?.filePartyRoles, [settings])

    // The old add/edit route-forms navigate back with { reload: true } after saving; refresh the list then.
    useEffect(() => {
        if (location?.state?.reload) {
            props.reset?.()
        }
    }, [location?.state?.reload])

    if (!roles || !props.data) {
        return <Loading />
    }

    const populatedRoles = roles.filter(role => props.data.some(party => matchesRole(role, party)))

    return (
        <div className={styles.container}>
            {props.data.length === 0 ? (
                <MessageBar intent="info" className={styles.alert}>
                    {translate('file.pivot.party.list.empty')}
                </MessageBar>
            ) : (
                populatedRoles.map(role => (
                    <FilePartySection
                        key={role.type}
                        role={role}
                        parties={props.data.filter(party => matchesRole(role, party))}
                        file={props.file}
                        roles={roles}
                        reset={props.reset}
                    />
                ))
            )}

            <div className={styles.add}>
                <PartyConnectButton appearance="primary" fileId={props.file.id} roles={roles} reset={props.reset} />
            </div>
        </div>
    )
}
